import { Location } from '@angular/common';
import { Component, Inject, Input, OnInit, ViewChild, ViewEncapsulation } from '@angular/core';
import { MatDialog } from '@angular/material/dialog';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Store } from '@ngrx/store';
import { BehaviorSubject } from 'rxjs';
import { filter, map, switchMap } from 'rxjs/operators';
import { APP_CONFIG, Settings } from 'src/app/config/settings';
import { WINDOW } from 'src/app/core/core.module';
import { IdentityService } from 'src/app/core/identity.service';
import {
    MarginCalculatorAuthService,
    UserCredentials
} from 'src/app/margin-calculator/services/margin-calculator-auth.service';
import { loadJobDetails } from 'src/app/recruiting/jobs/details/components/store/actions/job-details.actions';
import { jobDetailsLoaded } from 'src/app/recruiting/jobs/details/components/store/selectors/job-details.selectors';
import { JobDetails } from 'src/app/recruiting/jobs/details/models/job-details.model';
import { JobsRoutesHelper } from 'src/app/recruiting/jobs/jobs-routes-helper';
import { showToasterMessage } from 'src/app/shared/actions/toaster.actions';
import { HospitalType } from 'src/app/shared/lib/facility-rate/models/hospital-types.enum';
import { EmploymentTypeCode } from 'src/app/shared/models/enums/employment-type-code.enum';
import { FeatureFlag } from 'src/app/shared/models/enums/feature-flag';
import { NightCallBillRateTypeEnum } from 'src/app/shared/models/enums/job-night-call-bill-rate-type.enum';
import { PayPeriodTypeEnum } from 'src/app/shared/models/enums/pay-period-type.enum';
import { ExternalJobDisplayInputDto } from 'src/app/shared/models/external-job-display-input.dto';
import { ExternalJobDisplayService } from 'src/app/shared/services/external-job-display.service';
import { LinkSampleMarginToJobComponent } from '../../../dialog/nova-link-sample-margin-to-job.component';
import { UnitDescriptionSecureWindowService } from 'src/app/shared/services/unit-description-secure-window.service';
import { LDFeatureManager } from 'src/app/shared/services/ld-feature-manager';
import { ExternalJobDisplayPayIntroDto } from 'src/app/shared/models/external-job-display-pay-intro.dto';
import { Lookup } from 'src/app/shared/models/lookup.model';

@UntilDestroy()
@Component({
    selector: 'nova-recruiting-job-details-job-section',
    styleUrls: [
        './../../../../../../quick-quote/quick-quote.component.scss',
        './jobs-details-job-section.component.scss',
        '../../../../../../../shared/components/job-details-modal-tabs/job-details-modal-tabs.component.scss'
    ],
    templateUrl: './jobs-details-job-section.component.html',
    encapsulation: ViewEncapsulation.None
})
export class JobsDetailsJobSectionComponent implements OnInit {
    showMap = false;

    @ViewChild('gmap')
    gmapElement: any;

    map: google.maps.Map;

    currentUserFullName: string;

    job: JobDetails;

    @Input()
    id: number;

    @Input()
    showBackButton = true;

    loading = false;

    whatsThis = false;

    mapStatus = false;

    readMore = false;

    isUserAllowedMargin: boolean;

    showSampleMargin: boolean;

    showNewMarginCalculator: boolean;

    readonly routesHelper = JobsRoutesHelper;
    accountManager: any;

    displayConfigData: ExternalJobDisplayInputDto;
    payFormat = '';
    externalJobDisplayPayIntros: ExternalJobDisplayPayIntroDto[] = [];
    payShiftTypes: Lookup[] = [
        { id: 1, name: 'Per Hour' },
        { id: 2, name: 'Per Shift' },
        { id: 3, name: 'Per Day' },
        { id: 4, name: 'Per Week' },
        { id: 7, name: 'Per Weekend' },
        { id: 5, name: 'Per Year' },
        { id: 6, name: 'Per Contract' }
    ];

    readonly featureFlag = FeatureFlag;

    isLocums1099Job$ = new BehaviorSubject<boolean>(false);
    isLocumsEmploymentType$ = new BehaviorSubject<boolean>(false);
    nightCallBillRateTypeDisplay = '';
    billRateLabel = '';
    payRangeLabel = '';
    payFormatPeriodSpecification = '';
    payRangePeriodSpecification = '';
    weekendCallPeriodSpecification = '';
    startDate = '';
    showStartDate = true;
    isPermanentOrWorkforceDisruptionJob = false;
    isPermanentEmploymentType = false;
    isNovaCorehireAmJobsDetailsUpdatesEnabled = false;
    showShiftTBD = false;

    get showJobUnitRequirements(): boolean {
        return !this.isLocumsEmploymentType$.value;
    }

    get showAyaPaidBonus(): boolean {
        return this.job?.ayaPaidBonus > 0;
    }

    constructor(
        private readonly _identityService: IdentityService,
        @Inject(APP_CONFIG) private readonly _settings: Settings,
        private readonly _store: Store,
        private readonly _marginAuth: MarginCalculatorAuthService,
        private readonly _dialog: MatDialog,
        @Inject(WINDOW) private readonly _window: Window,
        private readonly _externalJobDisplayService: ExternalJobDisplayService,
        private readonly _location: Location,
        private readonly unitDescriptionSecureWindow: UnitDescriptionSecureWindowService,
        private readonly _lDFeatureManager: LDFeatureManager
    ) {}

    ngOnInit(): void {
        this.showNewMarginCalculator = this._settings.showNewMarginCalculator;

        const identity = this._identityService.current;
        this.currentUserFullName = `${identity.firstName} ${identity.lastName}`;

        this.showSampleMargin = identity.permissionsToken.indexOf('Recruiting:Jobs-SampleMargin$c') !== -1;

        this._marginAuth
            .getUserCredentials()
            .pipe(
                map((response: UserCredentials) =>
                    response === null ? false : response.isRecruiter || response.isNurseContract
                )
            )
            .subscribe((e) => (this.isUserAllowedMargin = e));

        this.loading = true;

        this._store.dispatch(loadJobDetails({ jobId: this.id }));

        this._store
            .select(jobDetailsLoaded)
            .pipe(untilDestroyed(this))
            .subscribe((e) => {
                if (e.success) {
                    this.job = e.job;
                    this.jobUpdated();
                    this.updateNightCallBillRateTypeDisplay();
                    this.updateLabelsAndPeriodSpecificationForLocums();
                    this.isPermanentOrWorkforceDisruptionJob =
                        this.job?.employmentTypeCode === EmploymentTypeCode.Staff ||
                        this.job?.employmentTypeCode === EmploymentTypeCode.WorkforceDisruption;
                    this.isPermanentEmploymentType = this.job?.employmentTypeCode === EmploymentTypeCode.Staff;
                    this.updateTextOnStartDateBasedOnEmploymentType();

                    this.showStartDate =
                        !this.isPermanentEmploymentType || (this.isPermanentEmploymentType && !!this.job?.startDate);
                    this.showShiftTBD = this.isPermanentEmploymentType && !this.job?.isShiftInfoAvailable;

                    this.loading = false;
                }
            });

        this._externalJobDisplayService
            .get([this.id])
            .pipe(
                untilDestroyed(this),
                switchMap((configData) => {
                    this.displayConfigData = configData;
                    return this._externalJobDisplayService.getPayIntros().pipe(untilDestroyed(this));
                })
            )
            .subscribe((payIntroData) => {
                if (payIntroData && this.displayConfigData) {
                    this.externalJobDisplayPayIntros = payIntroData;
                    this.payFormat = this._externalJobDisplayService.updatePayFormatDisplay(
                        this.externalJobDisplayPayIntros,
                        this.payShiftTypes,
                        this.displayConfigData
                    );
                }
            });

        this._lDFeatureManager
            .isEnabled(FeatureFlag.NovaCorehireAmJobsDetailsUpdates)
            .pipe(untilDestroyed(this))
            .subscribe((flagIsEnabled) => {
                this.isNovaCorehireAmJobsDetailsUpdatesEnabled = flagIsEnabled;
            });
    }

    toast(message: string) {
        this._store.dispatch(showToasterMessage({ message: message, duration: 5000 }));
    }

    linkMargin(jobId: number = null): void {
        this._dialog
            .open(LinkSampleMarginToJobComponent, {
                panelClass: 'nova__dialog',
                data: {
                    jobId: jobId
                }
            })
            .afterClosed()
            .pipe(filter((result) => result))
            .subscribe({
                next: (data) => {
                    this.toast('Sample margin ' + data.sampleMarginId + ' linked to job ' + jobId);
                    this.job = {
                        ...this.job,
                        sampleMarginId: data.sampleMarginId
                    };
                },
                error: (err) => {
                    if (err) {
                        this.toast(err);
                    }
                }
            });
    }

    sampleMargin(id: number, sampleMarginId: number): void {
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${id}/margin/${sampleMarginId}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    createNewMargin(jobId: number): void {
        this._window.open(
            `${this._window.location.origin}/#/margin-calculator/${this._identityService.current.contactId}/job/${jobId}`,
            '_blank',
            'width=1366,height=768,resizable=1,scrollbars=1,titlebar=0,location=0,status=0'
        );
    }

    newMarginCalcTooltip(): string {
        if (this.disableNewMarginCalc()) {
            if (!this.job.sampleMarginId) {
                return 'A sample margin is required for Workforce Disruption jobs. Please reach out to a recruitment manager to have one created';
            } else {
                return 'Please use the Sample Margin for Workforce Disruption jobs.';
            }
        }
        return '';
    }

    disableNewMarginCalc(): boolean {
        const isWfd =
            this.job.hospitalType === HospitalType.WorkforceDisruptionClinical ||
            this.job.hospitalType === HospitalType.WorkforceDisruptionNonClinical;
        return isWfd && !this.showSampleMargin;
    }

    openUnitLink($event: MouseEvent, unitFormId: string) {
        $event.preventDefault();
        const url = this.routesHelper.getUnitLink(
            unitFormId,
            this._identityService.current.contactId,
            this._settings.ayacDomain
        );
        this.unitDescriptionSecureWindow.open(url, '_blank', true);
    }

    close(): void {
        this._location.back();
    }

    jobUpdated(): void {
        this.isLocums1099Job$.next(this.job?.employmentTypeCode === EmploymentTypeCode.LocumTenens1099);
        this.isLocumsEmploymentType$.next(
            this.job?.employmentTypeCode === EmploymentTypeCode.LocumTenens1099 ||
                this.job?.employmentTypeCode === EmploymentTypeCode.LocumTenensW2
        );
    }

    updateNightCallBillRateTypeDisplay(): void {
        this.nightCallBillRateTypeDisplay =
            this.job?.additionalBillRate?.nightCallBillRateType === NightCallBillRateTypeEnum.PerShift
                ? '/shift'
                : '/hour';
    }

    updateLabelsAndPeriodSpecificationForLocums(): void {
        if (this.isLocumsEmploymentType$.value) {
            this.billRateLabel = 'Bill Rate';
            this.payRangeLabel = 'Pay Range';
            this.weekendCallPeriodSpecification = '/hour';
            this.payFormatPeriodSpecification =
                this.job.payPeriodTypeId === null || this.job.payPeriodTypeId === PayPeriodTypeEnum.Hourly
                    ? '/hour'
                    : '/day';
            this.payRangePeriodSpecification =
                this.job.payPeriodTypeId === null || this.job.payPeriodTypeId === PayPeriodTypeEnum.Hourly
                    ? ''
                    : '/day';
        } else {
            this.billRateLabel = 'Hourly Bill Rate';
            this.payRangeLabel = 'Weekly Pay Range';
            this.weekendCallPeriodSpecification = '';
            this.payFormatPeriodSpecification = '';
            this.payRangePeriodSpecification = '';
        }
    }

    updateTextOnStartDateBasedOnEmploymentType(): void {
        switch (this.job?.employmentTypeCode) {
            case EmploymentTypeCode.WorkforceDisruption:
                this.startDate = '"tentative" externally';
                break;
            case EmploymentTypeCode.Staff:
                this.startDate = '"flexible" externally';
                break;
        }
    }
}
